import clsx from 'clsx';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import { deserialize, type DocumentJSON } from '@gitbook/slate';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    ChatBubble,
    ChatColumn,
    ChatDisclosure,
    ChatLine,
    renderIcon,
    Tooltip,
} from '@gitbook/spine-react';

import { useCurrentOrganizationId } from '../../state';
import { AGENT_TASKS_DEF, isChangeRequestRunTask } from '../Agent';
import { AgentChartSeriesProvider, createAgentChartPlugin } from './agentChartPlugin';
import { AgentMessageActions } from './AgentMessageActions';
import { AgentWorkingIndicator, getActivityLabel } from './AgentWorkingIndicator';
import { AttachmentsSummary } from './AttachmentsSummary';
import { useHasBackgroundAgents } from './BackgroundAgentsContext';
import { EditorContextSummary } from './EditorContextSummary';
import { MessageDocument } from './MessageDocument';
import { isLiveToolCall, ToolCallsSummary } from './ToolCallsSummary';

export type AgentChatFeedbackInput = {
    responseId: string;
    sentiment: 'good' | 'bad';
};

/**
 * Display a list of AI messages.
 */
export function AgentChatMessages(props: {
    messages: api.AIMessage[];

    /**
     * If true, the chat is being loaded/processed.
     */
    loading?: boolean;

    /**
     * What the agent is currently doing, shown by the working indicator on the active message.
     */
    activity?: api.AgentActivity | null;

    /**
     * Callback to track when a message is rated.
     */
    onTrackMessageFeedback?: (input: AgentChatFeedbackInput) => void;

    /**
     * Whether the whole conversation is one autonomous run published to be read. Its messages then
     * carry no task banner: every one of them would name the same task, which is the conversation
     * itself — the tray under it says what it is, once.
     */
    consultOnly?: boolean;

    /** Shown at the end of the last message when the agent wrote it, above its feedback. */
    lastMessageFooter?: React.ReactNode;
}) {
    const {
        messages,
        onTrackMessageFeedback,
        loading = false,
        activity = null,
        consultOnly = false,
        lastMessageFooter,
    } = props;

    return (
        <>
            {getMessageEntries(messages).map((entry, index) => (
                <AgentChatMessage
                    key={entry.message.id}
                    message={entry.message}
                    loading={
                        loading &&
                        entry.message.role === api.AIMessageRole.Assistant &&
                        index === messages.length - 1
                    }
                    activity={activity}
                    isFirstMessage={index === 0}
                    isLastMessage={index === messages.length - 1}
                    onFeedback={onTrackMessageFeedback}
                    previousAgentMessage={entry.previousAgentMessage}
                    consultOnly={consultOnly}
                    isRepeatedSelection={entry.isRepeatedSelection}
                    footer={index === messages.length - 1 ? lastMessageFooter : undefined}
                />
            ))}
        </>
    );
}

/**
 * Each message with what its rendering needs of the ones before it: the agent's last message, and
 * whether the user is replying with the editor selection they already sent — the selection chip
 * then stays on the message that introduced it.
 */
function getMessageEntries(messages: api.AIMessage[]): {
    message: api.AIMessage;
    previousAgentMessage: api.AIMessage | null;
    isRepeatedSelection: boolean;
}[] {
    const entries: ReturnType<typeof getMessageEntries> = [];
    let previousAgentMessage: api.AIMessage | null = null;
    let previousUserSelectionSignature: string | null = null;

    for (const message of messages) {
        const userSelectionSignature =
            message.role === api.AIMessageRole.User ? getMessageSelectionSignature(message) : null;
        entries.push({
            message,
            previousAgentMessage,
            isRepeatedSelection:
                Boolean(userSelectionSignature) &&
                userSelectionSignature === previousUserSelectionSignature,
        });

        if (message.role === api.AIMessageRole.Assistant) {
            previousAgentMessage = message;
        } else if (message.role === api.AIMessageRole.User) {
            previousUserSelectionSignature = userSelectionSignature;
        }
    }

    return entries;
}

function AgentChatMessage(props: {
    message: api.AIMessage;
    previousAgentMessage: api.AIMessage | null;
    loading: boolean;
    activity: api.AgentActivity | null;
    isFirstMessage: boolean;
    isLastMessage: boolean;
    onFeedback: ((input: AgentChatFeedbackInput) => void) | undefined;
    isRepeatedSelection: boolean;
    consultOnly: boolean;
    footer: React.ReactNode;
}) {
    const {
        message,
        previousAgentMessage,
        loading,
        activity,
        isFirstMessage,
        isLastMessage,
        onFeedback,
        isRepeatedSelection,
        consultOnly,
        footer,
    } = props;
    const hasBackgroundAgents = useHasBackgroundAgents();
    const organizationId = useCurrentOrganizationId();
    const plugins = React.useMemo(
        () => (organizationId ? [createAgentChartPlugin({ organizationId, loading })] : undefined),
        [organizationId, loading]
    );

    // The task to name above the message, if it is worth naming there. What the whole conversation
    // is is not: a run the agent was given a change request for is named once, in the tray under
    // the transcript, which also says the one thing a banner never did — that a review can only be
    // read, and that an implementation goes on working after the window is closed.
    const bannerTask =
        message.task && !consultOnly && !isChangeRequestRunTask(message.task) ? message.task : null;

    const steps = React.useMemo(() => mergeMessageSteps(message.steps), [message.steps]);
    const answerStart = React.useMemo(() => getAnswerStart(steps, loading), [steps, loading]);

    // The runtime handing the agent something, not a person saying it. Today that is the reports of
    // background agents coming back after the turn which started them had ended, and the line says
    // so only for a conversation that has some — anything else the runtime ever delivers is its own
    // machinery, and naming it after this feature would be worse than not drawing it at all.
    if (message.role === api.AIMessageRole.Developer) {
        return hasBackgroundAgents ? (
            <ChatColumn>
                <ChatLine icon={Icon.Users}>Background agents reported back</ChatLine>
            </ChatColumn>
        ) : null;
    }

    return (
        <div className="flex flex-col gap-2">
            {bannerTask ? <TaskBanner task={bannerTask} loading={loading} /> : null}
            <ChatColumn
                className={clsx(
                    // The conversation's own padding is on its first and last message rather than
                    // on the scroll container, so a banner can stick flush with the top.
                    isFirstMessage && !bannerTask ? 'pt-4' : null,
                    isLastMessage ? 'pb-4' : null
                )}
            >
                {message.role === api.AIMessageRole.User ? (
                    <ChatBubble
                        footer={
                            <AgentMessageActions
                                getText={() => getUserMessageText(message)}
                                {...(message.createdAt ? { date: message.createdAt } : {})}
                            />
                        }
                    >
                        {steps.map((step, index) => (
                            <React.Fragment key={index}>
                                {step.content && step.content.nodes.length > 0 ? (
                                    <MessageDocument content={step.content} />
                                ) : null}
                                {(!isRepeatedSelection && step.context?.selection?.content) ||
                                step.context?.comments?.length ? (
                                    <EditorContextSummary
                                        selectedText={
                                            isRepeatedSelection
                                                ? undefined
                                                : step.context?.selection?.content
                                        }
                                        comments={step.context?.comments}
                                    />
                                ) : null}
                                {step.attachments && step.attachments.length > 0 ? (
                                    <AttachmentsSummary
                                        attachments={step.attachments}
                                        size="large"
                                    />
                                ) : null}
                            </React.Fragment>
                        ))}
                    </ChatBubble>
                ) : (
                    <AgentChartSeriesProvider>
                        <div className="flex min-w-0 flex-col text-ui-base text-neutral-high select-text">
                            {message.response &&
                            !message.response.previous &&
                            previousAgentMessage &&
                            // If the previous agent message was aborted, we don't need to show the
                            // expired conversation warning
                            !previousAgentMessage.response?.stopReason ? (
                                <ExpiredConversationWarning />
                            ) : null}
                            <MessageWork
                                steps={steps}
                                answerStart={answerStart}
                                loading={loading}
                                activity={activity}
                                plugins={plugins}
                            />
                            {steps.map((step, index) => {
                                const liveToolCalls = step.toolCalls?.filter(isLiveToolCall) ?? [];
                                return (
                                    <React.Fragment key={index}>
                                        {index >= answerStart && hasNodes(step.content) ? (
                                            <MessageDocument
                                                content={step.content}
                                                className="py-1.5"
                                                plugins={plugins}
                                            />
                                        ) : null}
                                        {liveToolCalls.length > 0 ? (
                                            <ToolCallsSummary
                                                toolCalls={liveToolCalls}
                                                loading={loading && index === steps.length - 1}
                                            />
                                        ) : null}
                                        {step.attachments && step.attachments.length > 0 ? (
                                            <AttachmentsSummary
                                                attachments={step.attachments}
                                                size="large"
                                                className="py-1.5"
                                            />
                                        ) : null}
                                    </React.Fragment>
                                );
                            })}
                            {message.response?.stopReason ? <AbortedConversationWarning /> : null}
                            {footer ? <div className="py-3">{footer}</div> : null}
                            {message.response?.id && isLastMessage && !loading && onFeedback ? (
                                <MessageFeedback
                                    onSubmit={(sentiment) =>
                                        onFeedback({
                                            responseId: message.response?.id ?? '',
                                            sentiment,
                                        })
                                    }
                                />
                            ) : null}
                        </div>
                    </AgentChartSeriesProvider>
                )}
            </ChatColumn>
        </div>
    );
}

/**
 * The task a message answers, named above it and kept in view while the message scrolls.
 */
function TaskBanner(props: { task: api.AIMessageTask; loading: boolean }) {
    const { task, loading } = props;
    const definition = AGENT_TASKS_DEF[task.type];

    return (
        <div className="sticky top-0 right-0 left-0 z-20 flex flex-row">
            <div className="mt-[-1px] flex flex-1 flex-row items-center gap-2 border-t border-b bg-muted px-4.5 py-1.5">
                {renderIcon(definition.icon, {
                    className: clsx('size-4 text-muted', loading ? 'animate-pulse' : null),
                })}
                <div
                    className={clsx(
                        'flex-1 text-ui-small text-muted',
                        loading ? 'animate-pulse' : null
                    )}
                >
                    {loading ? definition.processingLabel : definition.label}
                </div>
            </div>
        </div>
    );
}

/**
 * Plain text of a sent user message, for the clipboard: its steps' content in order.
 */
function getUserMessageText(message: api.AIMessage): string {
    return message.steps
        .map((step) => (step.content ? deserialize(step.content as DocumentJSON).text : ''))
        .filter(Boolean)
        .join('\n\n');
}

function MessageFeedback(props: {
    onSubmit: (sentiment: AgentChatFeedbackInput['sentiment']) => void;
}) {
    const { onSubmit } = props;
    const [submitted, setSubmitted] = React.useState<null | AgentChatFeedbackInput['sentiment']>(
        null
    );

    const onPressSubmit = (sentiment: AgentChatFeedbackInput['sentiment']) => {
        if (submitted) {
            setSubmitted(null);
        } else {
            onSubmit(sentiment);
            setSubmitted(sentiment);
        }
    };

    return (
        <div className="flex items-center gap-1 pt-1">
            {submitted !== 'bad' ? (
                <Button
                    size="xsmall"
                    leadingIcon={Icon.ThumbsUp}
                    active={submitted === 'good'}
                    iconOnly
                    label="Good response"
                    kind="blank"
                    onPress={() => {
                        onPressSubmit('good');
                    }}
                />
            ) : null}
            {submitted !== 'good' ? (
                <Button
                    size="xsmall"
                    leadingIcon={Icon.ThumbsDown}
                    active={submitted === 'bad'}
                    iconOnly
                    label="Bad response"
                    kind="blank"
                    onPress={() => {
                        onPressSubmit('bad');
                    }}
                />
            ) : null}
            {submitted ? (
                <span className="text-ui-small text-muted">Thank you for your feedback!</span>
            ) : null}
        </div>
    );
}

/**
 * The agent's reasoning, narration and tool calls before its answer, folded into one line.
 */
function MessageWork(props: {
    steps: api.AIMessageStep[];
    answerStart: number;
    loading: boolean;
    activity: api.AgentActivity | null;
    plugins: React.ComponentProps<typeof MessageDocument>['plugins'];
}) {
    const { steps, answerStart, loading, activity, plugins } = props;
    const working = loading && answerStart >= steps.length;
    const itemCount = countWorkItems(steps, answerStart);

    if (working && itemCount === 0) {
        return <AgentWorkingIndicator activity={activity} />;
    }

    const items = steps.map((step, index) => (
        <MessageWorkStep
            key={index}
            step={step}
            isAnswer={index >= answerStart}
            loading={loading}
            isLast={index === steps.length - 1}
            plugins={plugins}
        />
    ));

    // A single thing done needs no line summing it up.
    if (!working && itemCount <= 1) {
        return <>{items}</>;
    }

    return (
        <ChatDisclosure
            icon={
                <Icon.AIAgentAnimated
                    state={working ? 'loading' : 'default'}
                    aria-hidden="true"
                    role="presentation"
                />
            }
            loading={working}
            label={
                working
                    ? (getLatestReasoningTitle(steps) ?? getActivityLabel(activity))
                    : getWorkLabel(steps, itemCount)
            }
        >
            {items}
        </ChatDisclosure>
    );
}

function MessageWorkStep(props: {
    step: api.AIMessageStep;
    isAnswer: boolean;
    loading: boolean;
    isLast: boolean;
    plugins: React.ComponentProps<typeof MessageDocument>['plugins'];
}) {
    const { step, isAnswer, loading, isLast, plugins } = props;
    const toolCalls = step.toolCalls?.filter((toolCall) => !isLiveToolCall(toolCall)) ?? [];

    return (
        <>
            {hasNodes(step.reasoning) ? (
                <MessageReasoning
                    reasoningTimerange={step.timing?.reasoning}
                    reasoning={step.reasoning}
                    loading={loading && !hasNodes(step.content) && !step.toolCalls?.length}
                />
            ) : null}
            {!isAnswer && hasNodes(step.content) ? (
                <MessageDocument
                    content={step.content}
                    className="px-2 py-1"
                    typographyClassName="text-ui-small text-muted"
                    plugins={plugins}
                />
            ) : null}
            {toolCalls.length > 0 ? (
                <ToolCallsSummary toolCalls={toolCalls} loading={loading && isLast} />
            ) : null}
        </>
    );
}

/**
 * Index of the answer's first step: marked by newer models, else whatever follows the last tool.
 */
function getAnswerStart(steps: api.AIMessageStep[], loading: boolean): number {
    const finalAnswerIndex = steps.findIndex(
        (step) => step.phase === api.AIMessageStepPhase.FinalAnswer
    );
    if (finalAnswerIndex !== -1) {
        return finalAnswerIndex;
    }

    const start = steps.findLastIndex((step) => Boolean(step.toolCalls?.length)) + 1;
    // Mid-stream, text marked as commentary narrates work still to come, not an answer.
    const hasAnswerText = steps
        .slice(start)
        .some((step) => hasNodes(step.content) && step.phase !== api.AIMessageStepPhase.Commentary);
    return loading && !hasAnswerText ? steps.length : start;
}

function countWorkItems(steps: api.AIMessageStep[], answerStart: number): number {
    return steps.reduce(
        (count, step, index) =>
            count +
            (step.reasoning ? getReasoningSections(step.reasoning).length : 0) +
            (index < answerStart && hasNodes(step.content) ? 1 : 0) +
            (step.toolCalls?.filter((toolCall) => !isLiveToolCall(toolCall)).length ?? 0),
        0
    );
}

/**
 * Every round of work opens with reasoning, so the span of it all is how long the agent worked.
 */
function getWorkLabel(steps: api.AIMessageStep[], itemCount: number): string {
    const timeranges = steps.flatMap((step) =>
        step.timing?.reasoning ? [step.timing.reasoning] : []
    );
    if (timeranges.length === 0) {
        return `Worked through ${itemCount} steps`;
    }

    const start = Math.min(...timeranges.map((range) => new Date(range.start).getTime()));
    const end = Math.max(...timeranges.map((range) => new Date(range.end).getTime()));
    return `Worked for ${formatDuration(end - start)}`;
}

function hasNodes(document: api.JSONDocument | undefined): document is api.JSONDocument {
    return Boolean(document && document.nodes.length > 0);
}

// Sub-second durations would round to a meaningless "0s", so show milliseconds under a second.
function formatDuration(durationMs: number): string {
    if (durationMs < 1000) {
        return `${Math.round(durationMs)}ms`;
    }
    const seconds = Math.round(durationMs / 1000);
    return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

/** The latest reasoning title: it says what the agent is doing better than the tool it runs. */
function getLatestReasoningTitle(steps: api.AIMessageStep[]): string | null {
    return (
        steps
            .flatMap((step) =>
                step.reasoning
                    ? getReasoningSections(step.reasoning).map((section) => section.title)
                    : []
            )
            .findLast((title) => title !== null) ?? null
    );
}

/** A reasoning summary's parts: OpenAI opens each with a bold title, often its only content. */
function getReasoningSections(
    reasoning: api.JSONDocument
): { title: string | null; body: api.JSONDocument['nodes'] }[] {
    const sections: ReturnType<typeof getReasoningSections> = [];

    for (const block of reasoning.nodes) {
        const title = getReasoningTitle(block);
        const current = sections.at(-1);
        if (title !== null) {
            sections.push({ title, body: [] });
        } else if (current) {
            current.body.push(block);
        } else {
            sections.push({ title: null, body: [block] });
        }
    }

    return sections;
}

function getReasoningTitle(block: api.JSONDocument['nodes'][number]): string | null {
    if (block.type !== 'paragraph') {
        return null;
    }

    let title = '';
    for (const child of block.nodes) {
        if (child.object !== 'text') {
            return null;
        }
        for (const leaf of child.leaves) {
            if (leaf.text.trim() && !leaf.marks.some((mark) => mark.type === 'bold')) {
                return null;
            }
            title += leaf.text;
        }
    }

    return title.trim() || null;
}

function MessageReasoning(props: {
    reasoningTimerange: api.Timerange | undefined;
    reasoning: api.JSONDocument;
    loading: boolean;
}) {
    const { reasoningTimerange, reasoning, loading } = props;
    const sections = getReasoningSections(reasoning);

    return (
        <>
            {sections.map((section, index) => {
                const sectionLoading = loading && index === sections.length - 1;
                const label =
                    section.title ??
                    (sectionLoading
                        ? 'Thinking…'
                        : `Thought for ${formatDuration(getDurationMs(reasoningTimerange))}`);

                if (section.body.length === 0) {
                    return (
                        <ChatLine key={index} icon={Icon.AIThinking} loading={sectionLoading}>
                            {label}
                        </ChatLine>
                    );
                }

                return (
                    <ChatDisclosure
                        key={index}
                        icon={Icon.AIThinking}
                        loading={sectionLoading}
                        label={label}
                    >
                        <MessageDocument
                            content={{ ...reasoning, nodes: section.body }}
                            className="px-2 py-1"
                            typographyClassName="text-ui-small text-muted"
                        />
                    </ChatDisclosure>
                );
            })}
        </>
    );
}

function getDurationMs(timerange: api.Timerange | undefined): number {
    return timerange ? new Date(timerange.end).getTime() - new Date(timerange.start).getTime() : 0;
}

function ExpiredConversationWarning() {
    return (
        <ChatLine icon={Icon.Alert} tone="warning">
            GitBook Agent conversation memory has{' '}
            <Tooltip label="After 30 days of inactivity, GitBook Agent conversations expire and GitBook Agent loses context on previous messages.">
                <span className="cursor-help underline decoration-dotted">expired</span>
            </Tooltip>
            .
        </ChatLine>
    );
}

function AbortedConversationWarning() {
    return (
        <ChatLine icon={Icon.Alert} tone="warning">
            The response was{' '}
            <Tooltip label="You stopped the response or an unexpected error occurred.">
                <span className="cursor-help underline decoration-dotted">stopped</span>
            </Tooltip>
            . The agent might not have finished the task.
        </ChatLine>
    );
}

/**
 * Merge consecutive message steps that are simple tool calls into a single step.
 * We do this so that we can display similar tool calls together.
 */
function mergeMessageSteps(steps: api.AIMessageStep[]) {
    const result: api.AIMessageStep[] = [];

    for (const step of steps) {
        if (isMessageStepContentEmpty(step)) {
            if (result[result.length - 1]) {
                result[result.length - 1] = {
                    ...result[result.length - 1],
                    toolCalls: (result[result.length - 1].toolCalls ?? []).concat(
                        step.toolCalls ?? []
                    ),
                };
            } else {
                result.push(step);
            }
        } else {
            result.push(step);
        }
    }

    return result;
}

function getMessageSelectionSignature(message: api.AIMessage) {
    const selection = message.steps.find((step) => step.context?.selection)?.context?.selection;

    if (!selection?.content) {
        return null;
    }

    return JSON.stringify(selection);
}

function isMessageStepContentEmpty(step: api.AIMessageStep) {
    return (
        (!step.reasoning || step.reasoning.nodes.length === 0) &&
        (!step.content || step.content.nodes.length === 0)
    );
}
